import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import { Search, Loader2, MapPin } from 'lucide-react';

// Fix de íconos de Leaflet con bundler (si no, el marcador no se ve)
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

const DEFAULT_CENTER = [-9.19, -75.02]; // Perú
const DEFAULT_ZOOM = 5;

/**
 * Mapa con buscador (Nominatim) y pin arrastrable.
 * Llama onChange({ location, coordinates }) cada vez que cambia la búsqueda o el pin.
 */
const LocationPicker = ({ onChange }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [coords, setCoords] = useState(null);
  const [error, setError] = useState('');

  const emit = (nextQuery, nextCoords) => {
    onChange?.({
      location: nextQuery,
      coordinates: nextCoords ? `${nextCoords.lat.toFixed(6)}, ${nextCoords.lng.toFixed(6)}` : '',
    });
  };

  const placePin = (lat, lng) => {
    const map = mapRef.current;
    if (!map) return;
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      const m = L.marker([lat, lng], { draggable: true }).addTo(map);
      m.on('dragend', () => {
        const p = m.getLatLng();
        const c = { lat: p.lat, lng: p.lng };
        setCoords(c);
        emit(query, c);
      });
      markerRef.current = m;
    }
    const c = { lat, lng };
    setCoords(c);
    emit(query, c);
  };

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);
    map.on('click', (e) => placePin(e.latlng.lat, e.latlng.lng));
    mapRef.current = map;
    // El contenedor a veces monta con tamaño 0; forzamos el recálculo.
    setTimeout(() => map.invalidateSize(), 250);
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setError('');
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'es' } });
      const data = await res.json();
      if (!data.length) {
        setError('No se encontró esa ubicación. Podés mover el pin a mano en el mapa.');
        return;
      }
      const lat = parseFloat(data[0].lat);
      const lng = parseFloat(data[0].lon);
      mapRef.current.setView([lat, lng], 15);
      placePin(lat, lng);
    } catch {
      setError('No se pudo buscar. Intentá de nuevo o mové el pin a mano.');
    } finally {
      setSearching(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      doSearch();
    }
  };

  return (
    <div>
      {/* Buscador */}
      <div className="flex gap-2 mb-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              emit(e.target.value, coords);
            }}
            onKeyDown={onKeyDown}
            placeholder="Busca tu ciudad, distrito o dirección…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          />
        </div>
        <button
          type="button"
          onClick={doSearch}
          disabled={searching}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Buscar
        </button>
      </div>

      {/* Mapa */}
      <div
        ref={containerRef}
        className="w-full h-64 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600 z-0"
      />

      {/* Estado */}
      <div className="mt-2 flex items-center gap-2 text-xs">
        {coords ? (
          <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400 font-medium">
            <MapPin className="w-3.5 h-3.5" />
            Ubicación marcada: {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
          </span>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">
            Busca tu zona o haz clic en el mapa para poner el pin. Podés arrastrarlo para ajustarlo.
          </span>
        )}
      </div>
      {error && <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{error}</p>}
    </div>
  );
};

export default LocationPicker;
